import { inject } from '@angular/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ActiveToast } from 'ngx-toastr'

import { AssetMini, EditorMarker, Subclip, Tag } from 'elements-sdk'
import { LoggerFactory } from 'common/log'
import { Time } from 'media/time'
import { PremiereRenderModalComponent, SequenceRenderParams, SequenceRenderScope } from 'panel/components/premiereRenderSequenceModal.component'
import { ActionSpec, BridgeError, Ref, UxpBridgeService, UxpHandle } from 'panel/services/uxpBridge.service'
import { GenericFileReader } from 'uploader/services/uploader.service'

import { BaseInterface, loadAssetContentsPages } from './base'
import { NodeFileReader } from './common'

const require = (window as any).nodeRequire

const TRACK_ITEM_TYPE_CLIP = 1
const PROJECT_ITEM_TYPE_BIN = 2
const MARKER_COLORS = ['green', 'red', 'purple', 'orange', 'yellow', 'white', 'blue', 'teal']

const LOG_NOTE_NS = 'premierePrivateProjectMetaData'
const LOG_NOTE_NS_URI = 'http://ns.adobe.com/premierePrivateProjectMetaData/1.0/'
const LOG_NOTE_COLUMN_ID = 'Column.Intrinsic.LogNote'
const RDF_NS = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#'
const XMPMETA_NS = 'adobe:ns:meta/'

// Some features (e.g. subclip import via createSubClipAction) require this
// Premiere version; below it the panel degrades and warns the user once.
const MIN_SUPPORTED_VERSION: [number, number] = [26, 3]

/** True if the dotted version string is older than [major, minor]. */
function isVersionBelow (version: string, [major, minor]: [number, number]): boolean {
  const [vMajor = 0, vMinor = 0] = version.split('.').map(Number)
  return vMajor < major || (vMajor === major && vMinor < minor)
}

function normaliseMediaPath (path: string): string {
  return path.replace(/\\/g, '/').toLowerCase()
}

function subclipImportOptionsForAsset (asset?: AssetMini): { takeVideo: boolean, takeAudio: boolean } {
  const streams = asset?.format.streams
  if (!streams?.length) {
    return { takeVideo: true, takeAudio: true }
  }
  const has = (type: string) => streams.some(stream => stream.codecType === type)
  const takeVideo = has('video')
  const takeAudio = has('audio')
  // All-or-nothing if neither matched (e.g. unexpected codec_type values).
  if (!takeVideo && !takeAudio) {
    return { takeVideo: true, takeAudio: true }
  }
  return { takeVideo, takeAudio }
}

export class UXPPanelInterface extends BaseInterface {
  override logger = inject(LoggerFactory).create('UXPPanelInterface')
  override native = true
  override uri = false
  override canUploadSequence = true
  override canToggleProxyMode = false

  private readonly bridge = inject(UxpBridgeService)
  private readonly ngbModal = inject(NgbModal)
  private currentImportFolder: string | null = null

  override systemPath: Record<string, string> = {
    USER_DATA: 'userData',
    MY_DOCUMENTS: 'documents',
    COMMON_FILES: 'home',
    APPLICATION: 'exe',
    EXTENSION: 'userData',
    HOST_APPLICATION: 'exe',
  }

  constructor () {
    super()
    this.platform = this.getPlatform()
    this.app = 'PPRO'
  }

  private versionWarningToast: ActiveToast<any> | null = null

  async init () {
    this.logger.info('UXP init')
    this.active = await this.bridge.isAvailable()
    if (!this.active) {
      this.logger.warn('UXP plugin not connected; panel will be inactive')
    } else {
      this.warnIfUnsupportedVersion()
    }
    this.bridge.onAvailabilityChange(available => {
      if (available !== this.active) {
        this.logger.info(`UXP plugin ${available ? 'connected' : 'disconnected'}`)
      }
      this.active = available
      if (available) {
        this.warnIfUnsupportedVersion()
      }
    })
  }

  /**
   * Warn if the connected Premiere is older than the minimum version some
   * panel features require. Dismiss the warning if reconnecting to a supported
   * version. Best-effort: a failed version read is ignored.
   */
  private async warnIfUnsupportedVersion (): Promise<void> {
    const version = await this.bridge.get<string>('uxp.host.version').catch(() => null)
    if (!version || !isVersionBelow(version, MIN_SUPPORTED_VERSION)) {
      if (this.versionWarningToast) {
        this.notify.dismiss(this.versionWarningToast)
        this.versionWarningToast = null
      }
      return
    }
    if (this.versionWarningToast) {
      return
    }
    this.logger.warn(`Premiere ${version} is below the supported ${MIN_SUPPORTED_VERSION.join('.')}`)
    this.versionWarningToast = this.notify.custom(
      'warning',
      this.translate.instant('Premiere Pro update recommended'),
      this.translate.instant(
        'Some panel features (such as subclip import) require Premiere Pro 26.3 or newer.',
      ),
      { sticky: true },
    )
  }

  getFS () {
    const fs = require('fs')
    return {
      ...fs,
      writeFile: (path: string, data: any): Promise<string> =>
        new Promise((resolve, reject) => {
          fs.writeFile(path, data, (err: unknown) => {
            if (err) {
              reject(err)
            } else {
              resolve(path)
            }
          })
        }),
    }
  }

  getApp () {
    return 'PPRO'
  }

  getPlatform () {
    const platform = require('os').platform()
    return platform === 'win32' ? 'win' : 'osx'
  }

  getSystemPath (name: string) {
    // `name` may be a CEP-style key (mapped via `systemPath`) or a raw
    // Electron app.getPath() name.
    const electron = require('@electron/remote')
    const app = electron.app
    const electronName = this.systemPath[name] ?? name
    try {
      return app.getPath(electronName)
    } catch {
      return app.getPath('userData')
    }
  }

  hasRestart () {
    return true
  }

  async restart () {
    this.logger.info('restart() — requesting fresh URL from client')
    const electron = require('electron')
    try {
      const url = await electron.ipcRenderer.invoke('panel-bridge:refresh-url')
      if (url) {
        window.location.href = url
        return
      }
    } catch (error: any) {
      this.logger.warn('Failed to get fresh URL from client:', error?.message ?? error)
      if (error?.code === 'LOGIN_REQUIRED') {
        electron.ipcRenderer.send('panel-bridge:close')
        return
      }
    }
    location.reload()
  }

  private async getProject (): Promise<Ref> {
    const project = await this.bridge.ppro('Project')
    const active = await project.callRef('getActiveProject')
    if (!active) {
      throw new Error('No active Premiere project')
    }
    return active
  }

  private async getActiveSequence (project?: Ref): Promise<Ref | null> {
    project = project ?? await this.getProject()
    return project.callRef('getActiveSequence')
  }

  /** Cast a ProjectItem to a subtype via its static `cast`; null if not that subtype. */
  private async castItem (className: string, item: Ref): Promise<Ref | null> {
    const ctor = await this.bridge.ppro(className)
    return ctor.callRef('cast', [item.handle]).catch(() => null)
  }

  private async asClipItem (item: Ref): Promise<Ref | null> {
    // Bins are never clips; skip the cast attempt for them.
    const type = await item.get<number>('type').catch(() => undefined)
    if (type === PROJECT_ITEM_TYPE_BIN) {
      return null
    }
    return this.castItem('ClipProjectItem', item)
  }

  private async asFolderItem (item: Ref): Promise<Ref | null> {
    return this.castItem('FolderItem', item)
  }

  private async tickTimeFromSeconds (seconds: number): Promise<Ref> {
    const tickTime = await this.bridge.ppro('TickTime')
    const ref = await tickTime.callRef('createWithSeconds', [seconds])
    if (!ref) {
      throw new Error('Could not construct TickTime')
    }
    return ref
  }

  async importAssets (paths: string[]) {
    await this.bridge.withScope(async () => {
      const project = await this.getProject()
      const bin = await this.resolveImportBin(project)
      await project.call('importFiles', [paths, true, bin?.handle], { modal: true, modalText: 'Import media' })
    })
  }

  importPath (path: string) {
    return this.importAssets([path])
  }

  /**
   * Find or create the bin to import into, for `importFiles(..., targetBin)`.
   */
  private async resolveImportBin (project: Ref): Promise<Ref | null> {
    if (this.currentImportFolder === null) {
      const insertionBin = await this.getInsertionBin(project)
      return insertionBin ?? project.callRef('getRootItem')
    }

    let bin = await project.callRef('getRootItem')
    for (const name of this.currentImportFolder.split('/').filter(Boolean)) {
      if (!bin) {
        return null
      }
      bin = await this.findChildBin(bin, name) ?? await this.createBin(project, bin, name)
    }
    return bin
  }

  private getInsertionBin (project: Ref): Promise<Ref | null> {
    return project.callRef('getInsertionBin').catch(() => null)
  }

  private async getInsertionBinPath (project: Ref): Promise<string> {
    const names: string[] = []
    let item = await this.getInsertionBin(project)

    // Bounded in case the parent chain ever fails to terminate.
    for (let depth = 0; item && depth < 64; depth++) {
      const parent = await item.callRef('getParentBin').catch(() => null)
      if (!parent) {
        break
      }
      names.unshift(await item.get<string>('name'))
      item = parent
    }

    return names.join('/')
  }

  async importSubclips (path: string, subclips: Subclip[], targetBinHandle?: UxpHandle) {
    this.logger.info('Importing subclips:', path, subclips, 'targetBin:', targetBinHandle)
    await this.bridge.withScope(async () => {
      const clip = await this.waitForClipByMediaPath(path)
      if (!clip) {
        this.logger.warn('No project item matched media path for subclips', path)
        return
      }
      this.logger.info('Found clip for subclips, creating', subclips.length, 'subclips')
      const project = await this.getProject()

      // Subclips are created beside the source clip
      const clipAsItem = await this.castItem('ProjectItem', clip) ?? clip
      const parentBin = await clipAsItem.callRef('getParentBin')
      const sourceBin = parentBin ? await this.asFolderItem(parentBin) : null
      const existingIds = new Set<string>()
      if (sourceBin) {
        for (const item of await sourceBin.callRefList('getItems')) {
          existingIds.add(await item.call<string>('getId'))
        }
      }

      const createActions: ActionSpec[] = []
      for (const subclip of subclips) {
        const startTime = await this.tickTimeFromSeconds(subclip.tIn)
        const endTime = await this.tickTimeFromSeconds(subclip.tOut)
        createActions.push({
          target: clip.handle,
          method: 'createSubClipAction',
          args: [
            subclip.name,
            startTime.handle,
            endTime.handle,
            false,
            subclipImportOptionsForAsset(subclip.asset),
          ],
        })
      }

      if (!createActions.length) {
        return
      }

      await this.executeActions(project, createActions, 'Create subclips')
      this.logger.info('Subclips created, now moving to target bin')

      // Move newly created subclips to the current insertion bin (matching CEP
      // behavior which uses app.project.getInsertionBin()).
      const targetBin = targetBinHandle
        ? await this.asFolderItem(this.bridge.ref(targetBinHandle))
        : null
      this.logger.info('Source bin:', sourceBin?.handle, 'Target bin:', targetBin?.handle)
      if (!sourceBin || !targetBin || sourceBin.handle === targetBin.handle) {
        this.logger.info('Skipping move: sourceBin and targetBin are the same or missing')
        return
      }

      const newItems: Ref[] = []
      for (const item of await sourceBin.callRefList('getItems')) {
        if (!existingIds.has(await item.call<string>('getId'))) {
          newItems.push(item)
        }
      }
      this.logger.info('Found', newItems.length, 'new items to move')

      if (newItems.length) {
        const moveActions: ActionSpec[] = newItems.map(item => ({
          target: targetBin.handle,
          method: 'createMoveItemAction',
          args: [item.handle, targetBin.handle],
        }))
        await this.executeActions(project, moveActions, 'Move subclips')
        this.logger.info('Subclips moved')
      }
    })
  }

  async importDirs (dirs, folder?) {
    const overrideBefore = this.currentImportFolder
    folder = folder ?? await this.getImportFolder() ?? ''
    try {
      for (const dir of dirs) {
        await loadAssetContentsPages(this.api, dir, async (bundles, directories) => {
          const importFolder = `${folder}/${dir.name}`
          if (bundles.length) {
            await this.setImportFolder(importFolder)
            await this.importBundles(bundles)
          }
          if (directories.length) {
            await this.importDirs(directories, importFolder)
          }
        })
      }
    } catch (error) {
      this.reportError('Import to Premiere failed', error)
    } finally {
      this.currentImportFolder = overrideBefore
    }
  }

  override async importBundles (bundles) {
    return this.withBusyState(async () => {
      try {
        const subclips = await this.querySubclipImport(bundles)
        const importFolder = this.currentImportFolder

        let targetBinHandle: UxpHandle | undefined
        if (subclips.length) {
          const project = await this.getProject()
          const insertionBin = await this.getInsertionBin(project)
          targetBinHandle = insertionBin?.handle
          this.logger.info('Pre-captured insertion bin for subclips:', targetBinHandle)
        }

        await super.importBundles(bundles)

        if (subclips.length) {
          for (const bundle of bundles) {
            const bundleSubclips = subclips.filter(item => item.asset.id === bundle.asset.id)
            if (!bundleSubclips.length) {
              continue
            }
            const url = await this.clientSideUrl.getPathUrl(bundle.mainfile.fullPath, this.platform)
            this.currentImportFolder = importFolder
            await this.importSubclips(url, bundleSubclips, targetBinHandle)
          }
        }
      } catch (error) {
        this.reportError('Import to Premiere failed', error)
      }
    })
  }

  async open (item) {
    const type = item.asset?.type

    if (type === 'fcpxml' || type === 'prproj') {
      if (!item.mainfile) {
        this.notify.error(this.translate.instant('Internal error: bundle mainfile unknown'))
        return
      }
      try {
        if (type === 'fcpxml') {
          await this.importEditorProject(item.mainfile.id)
        } else {
          await this.importFile(item.mainfile.fullPath)
        }
      } catch (error) {
        this.reportError('Import to Premiere failed', error)
      }
    } else if (['video', 'audio', 'image'].includes(type)) {
      await this.importBundles([item]).catch(error => this.reportError('Import to Premiere failed', error))
    }
  }

  async getImportFolder (): Promise<string | null> {
    if (this.currentImportFolder !== null) {
      return this.currentImportFolder
    }
    return this.bridge.withScope(async () => this.getInsertionBinPath(await this.getProject()))
      .catch(() => '')
  }

  async setImportFolder (path?: string | null) {
    this.currentImportFolder = path ?? null
  }

  private async findChildBin (bin: Ref, name: string): Promise<Ref | null> {
    const items = await bin.callRefList('getItems')
    for (const item of items) {
      const itemName = await item.get<string>('name')
      const itemType = await item.get<number>('type')
      if (itemName === name && itemType === PROJECT_ITEM_TYPE_BIN) {
        // Cast to FolderItem: getItems() returns base ProjectItems whose
        // getItems()/createBinAction would otherwise be uncallable.
        return this.asFolderItem(item)
      }
    }
    return null
  }

  private async createBin (project: Ref, parent: Ref, name: string): Promise<Ref> {
    await this.executeActions(project, [
      { target: parent.handle, method: 'createBinAction', args: [name, true] },
    ], 'Create bin')
    const created = await this.findChildBin(parent, name)
    if (!created) {
      throw new Error(`Failed to create bin "${name}"`)
    }
    return created
  }

  /**
   * Build and commit action specs in one undoable transaction.
   *
   * A creator that rejects its arguments can return no action instead of
   * throwing, which the plugin skips — so compare the committed count and warn
   * rather than letting the operation look like it succeeded.
   */
  private async executeActions (project: Ref, actions: ActionSpec[], label: string) {
    const { committed } = await this.bridge.rpcExecuteActions(project.handle, actions, label)
    if (committed < actions.length) {
      this.logger.warn(
        `${label}: Premiere committed ${committed} of ${actions.length} actions`,
        actions.map(action => action.method),
      )
    }
  }

  async getActiveSequenceID (): Promise<string | null> {
    return this.bridge.withScope(async () => {
      const project = await this.getProject()
      const sequence = await this.getActiveSequence(project)
      if (!sequence) {
        return null
      }
      const projectGuid = await project.get<string>('guid')
      const sequenceGuid = await sequence.get<string>('guid')
      return `${projectGuid}:${sequenceGuid}`
    })
  }

  async getActiveSequenceTimestamp (): Promise<Time | null> {
    return this.bridge.withScope(async () => {
      const sequence = await this.getActiveSequence()
      if (!sequence) {
        return null
      }
      const position = await sequence.callRef('getPlayerPosition')
      if (!position) {
        return null
      }
      const seconds = await position.get<number>('seconds')
      return new Time(seconds)
    })
  }

  async setActiveSequenceTimestamp (t: Time): Promise<void> {
    await this.bridge.withScope(async () => {
      const sequence = await this.getActiveSequence()
      if (!sequence) {
        return
      }
      const tickTime = await this.tickTimeFromSeconds(t.valueOf())
      await sequence.call('setPlayerPosition', [tickTime.handle])
    })
  }

  async getActiveSequencePaths (): Promise<string[]> {
    return this.bridge.withScope(async () => {
      const sequence = await this.getActiveSequence()
      if (!sequence) {
        return []
      }

      const paths = new Set<string>()
      const trackCount = await sequence.call<number>('getVideoTrackCount')
      for (let i = 0; i < trackCount; i++) {
        const track = await sequence.callRef('getVideoTrack', [i])
        if (!track) {
          continue
        }
        const items = await track.callRefList('getTrackItems', [TRACK_ITEM_TYPE_CLIP, false])
        for (const item of items) {
          const projectItem = await item.callRef('getProjectItem')
          const clip = projectItem ? await this.asClipItem(projectItem) : null
          const path = clip ? await clip.call<string>('getMediaFilePath').catch(() => null) : null
          if (path) {
            paths.add(path)
          }
        }
      }
      return [...paths]
    })
  }

  async getProjectPaths (): Promise<string[]> {
    return this.bridge.withScope(async () => {
      const project = await this.getProject()
      const root = await project.callRef('getRootItem')
      if (!root) {
        return []
      }
      const paths = new Set<string>()
      await this.collectItemPaths(root, paths)
      return [...paths]
    })
  }

  private async collectItemPaths (container: Ref, paths: Set<string>): Promise<void> {
    const items = await container.callRefList('getItems')
    for (const item of items) {
      const type = await item.get<number>('type')
      if (type === PROJECT_ITEM_TYPE_BIN) {
        // Cast so getItems() is callable before recursing (see findChildBin).
        const folder = await this.asFolderItem(item)
        if (folder) {
          await this.collectItemPaths(folder, paths)
        }
      } else {
        const clip = await this.asClipItem(item)
        const path = clip ? await clip.call<string>('getMediaFilePath').catch(() => null) : null
        if (path) {
          paths.add(path)
        }
      }
    }
  }

  /**
   * Find ClipProjectItems matching `path`. Also returns every media path seen
   * during the walk, so callers can diagnose a path-format mismatch.
   */
  private async findClipsByMediaPath (
    project: Ref,
    path: string,
  ): Promise<{ matches: Ref[], seen: string[] }> {
    const root = await project.callRef('getRootItem')
    if (!root) {
      return { matches: [], seen: [] }
    }
    const target = normaliseMediaPath(path)
    const matches: Ref[] = []
    const seen: string[] = []
    await this.collectClipsByMediaPath(root, target, matches, seen)
    return { matches, seen }
  }

  /** Find the source clip by media path, retrying while an import settles. */
  private async waitForClipByMediaPath (
    path: string,
    attempts = 40,
    intervalMs = 250,
  ): Promise<Ref | null> {
    let lastSeen: string[] = []
    for (let i = 0; i < attempts; i++) {
      // Scope each attempt so a full tree walk's handles are freed before the
      // next poll rather than accumulating across all retries; keep the match.
      const match = await this.bridge.withScope(async scope => {
        const project = await this.getProject()
        const { matches, seen } = await this.findClipsByMediaPath(project, path)
        lastSeen = seen
        return matches.length ? scope.keep(matches[0] ?? null) : null
      })
      if (match) {
        return match
      }
      if (i < attempts - 1) {
        await this.delay(intervalMs)
      }
    }
    this.logger.warn(
      'Timed out waiting for imported clip by media path',
      { target: normaliseMediaPath(path), seenMediaPaths: lastSeen },
    )
    return null
  }

  private delay (ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  private async collectClipsByMediaPath (container: Ref, target: string, out: Ref[], seen?: string[]): Promise<void> {
    const items = await container.callRefList('getItems')
    for (const item of items) {
      const type = await item.get<number>('type').catch(() => undefined)
      if (type === PROJECT_ITEM_TYPE_BIN) {
        // Cast so getItems() is callable before recursing (see findChildBin).
        const folder = await this.asFolderItem(item)
        if (folder) {
          await this.collectClipsByMediaPath(folder, target, out, seen)
        }
        continue
      }
      const clip = await this.asClipItem(item)
      if (!clip) {
        continue
      }
      const mediaPath = await clip.call<string>('getMediaFilePath').catch(() => null)
      if (mediaPath) {
        seen?.push(mediaPath)
        // Substring match in either direction: client-mount vs server paths
        // mean either may be a prefix/superset of the other.
        const clipPath = normaliseMediaPath(mediaPath)
        if (clipPath.includes(target) || target.includes(clipPath)) {
          out.push(clip)
        }
      }
    }
  }

  async attachProxies (pathProxyMap: Record<string, string>) {
    await this.runOp('Attach proxies failed', () => this.bridge.withScope(async () => {
      const project = await this.getProject()
      let attached = 0
      let failed = 0
      for (const path of Object.keys(pathProxyMap)) {
        const { matches: clips } = await this.findClipsByMediaPath(project, path)
        for (const clip of clips) {
          // attachProxy is not undoable, so it runs outside a transaction.
          const ok = await clip.call<boolean>('attachProxy', [pathProxyMap[path], false]).catch(error => {
            this.logger.warn('attachProxy failed for', path, error)
            return false
          })
          if (ok) {
            attached++
          } else {
            failed++
          }
        }
      }
      this.logger.info(`attachProxies: ${attached} attached, ${failed} failed`)
      // No proxy-mode state to track: display is governed by Premiere's global
      // Enable Proxies toggle, which the UXP API does not expose.
    }), undefined)
  }

  async isProxyMode (): Promise<boolean> {
    return false
  }

  async setProxyMode (_enabled: boolean) {
    // No-op: UXP has no proxy display toggle.
  }

  private async findClipByPath (project: Ref, path: string): Promise<Ref | null> {
    const { matches } = await this.findClipsByMediaPath(project, path)
    return matches[0] ?? null
  }

  override async getMarkersForPath (path: string): Promise<EditorMarker[]> {
    return this.bridge.withScope(async () => {
      const project = await this.getProject()
      const clip = await this.findClipByPath(project, path)
      if (!clip) {
        return []
      }

      const markersStatic = await this.bridge.ppro('Markers')
      const markersObj = await markersStatic.callRef('getMarkers', [clip.handle])
      if (!markersObj) {
        return []
      }
      const markers = await markersObj.callRefList('getMarkers')

      const results: EditorMarker[] = []
      for (const marker of markers) {
        const start = await marker.callRef('getStart')
        const startSeconds = start ? await start.get<number>('seconds') : 0
        const duration = await marker.callRef('getDuration').catch(() => null)
        const durationSeconds = duration ? await duration.get<number>('seconds') : 0
        const colorIndex = await marker.call<number>('getColorIndex').catch(() => 0)
        results.push({
          title: await marker.call<string>('getName').catch(() => ''),
          text: await marker.call<string>('getComments').catch(() => ''),
          start: startSeconds,
          end: startSeconds + durationSeconds,
          color: MARKER_COLORS[colorIndex] ?? 'green',
        })
      }
      return results
    })
  }

  override async replaceMetadataForPath (path: string, markers: EditorMarker[], newTags: Tag[]): Promise<void> {
    return this.bridge.withScope(async () => {
      const project = await this.getProject()
      const clip = await this.findClipByPath(project, path)
      if (!clip) {
        this.logger.warn('No project item matched media path for metadata sync', path)
        return
      }

      const markersStatic = await this.bridge.ppro('Markers')
      const markersObj = await markersStatic.callRef('getMarkers', [clip.handle])
      if (!markersObj) {
        return
      }

      // Send specs, not pre-built Action handles: the creators read project
      // state and must run under the plugin's transaction lock (see executeActions).
      const actions: ActionSpec[] = []

      const markerKey = (ticks: string, name: string, comments: string): string =>
        JSON.stringify([ticks, name, comments])
      const existingColorByKey = new Map<string, number>()

      const existing = await markersObj.callRefList('getMarkers')
      for (const marker of existing) {
        const start = await marker.callRef('getStart')
        const startTicks = start ? await start.get<string>('ticks') : '0'
        const name = await marker.call<string>('getName').catch(() => '')
        const comments = await marker.call<string>('getComments').catch(() => '')
        const colorIndex = await marker.call<number>('getColorIndex').catch(() => 0)
        existingColorByKey.set(markerKey(startTicks, name, comments), colorIndex)

        actions.push({
          target: markersObj.handle,
          method: 'createRemoveMarkerAction',
          args: [marker.handle],
        })
      }

      // Build each marker independently so one malformed one doesn't abort the sync.
      const colorByKey = new Map<string, number>()
      for (const marker of markers) {
        try {
          const startTime = await this.tickTimeFromSeconds(marker.start)
          // createAddMarkerAction rejects null/undefined args ("Illegal Parameter
          // type"), and undefined serialises to null over the bridge — so pass a
          // zero-duration TickTime and an explicit "Comment" type.
          const durationSeconds = marker.end > marker.start ? marker.end - marker.start : 0
          const duration = await this.tickTimeFromSeconds(durationSeconds)
          actions.push({
            target: markersObj.handle,
            method: 'createAddMarkerAction',
            args: [marker.title, 'Comment', startTime.handle, duration.handle, marker.text],
          })
          // Determine color: prefer existing Premiere color, fall back to webui color.
          const ticks = await startTime.get<string>('ticks')
          const key = markerKey(ticks, marker.title, marker.text)
          const existingColor = existingColorByKey.get(key)
          const colorIndex = existingColor ?? MARKER_COLORS.indexOf(marker.color)
          if (colorIndex > 0) {
            colorByKey.set(key, colorIndex)
          }
        } catch (error) {
          this.logger.warn('Skipping marker that failed to build', marker.title, error)
        }
      }

      if (actions.length) {
        await this.executeActions(project, actions, 'Replace markers')
      }

      // Second pass: set colors on newly created markers. We re-fetch the markers and
      // match by ticks+name+comments since createAddMarkerAction doesn't return the marker ref.
      if (colorByKey.size > 0) {
        const colorActions: ActionSpec[] = []
        const newMarkers = await markersObj.callRefList('getMarkers')
        for (const markerRef of newMarkers) {
          const start = await markerRef.callRef('getStart')
          const startTicks = start ? await start.get<string>('ticks') : '0'
          const name = await markerRef.call<string>('getName').catch(() => '')
          const comments = await markerRef.call<string>('getComments').catch(() => '')
          const desiredColorIndex = colorByKey.get(markerKey(startTicks, name, comments))
          if (desiredColorIndex !== undefined) {
            colorActions.push({
              target: markerRef.handle,
              method: 'createSetColorByIndexAction',
              args: [desiredColorIndex],
            })
          }
        }
        if (colorActions.length) {
          await this.executeActions(project, colorActions, 'Set marker colors')
        }
      }

      await this.appendLogNoteTags(project, clip, newTags)
    })
  }

  private async appendLogNoteTags (project: Ref, clip: Ref, newTags: Tag[]): Promise<void> {
    if (!newTags.length) {
      return
    }
    const item = await this.castItem('ProjectItem', clip) ?? clip

    const metadataStatic = await this.bridge.ppro('Metadata')
    const columnsJson = await metadataStatic.call<string>('getProjectColumnsMetadata', [item.handle])
    const columns = JSON.parse(columnsJson || '[]') as { ColumnName: string, ColumnValue: string }[]
    const logNote = columns.find(c => c.ColumnName === 'Log Note')?.ColumnValue ?? ''

    const existingTokens = new Set(logNote.split(',').map(s => s.trim()))
    const newTagsStr = newTags
      .map(t => `[${t.name}]`)
      .filter(t => !existingTokens.has(t))
      .join(',')
    if (!newTagsStr) {
      return
    }
    const newLogNote = [logNote, newTagsStr].filter(Boolean).join(',')

    const updated = this.buildLogNoteXmp(newLogNote)
    await this.executeActions(project, [{
      target: metadataStatic.handle,
      method: 'createSetProjectMetadataAction',
      args: [item.handle, updated, [LOG_NOTE_COLUMN_ID]],
    }], 'Update log note')
  }

  /** Serialise a Log Note value into a complete XMP project-metadata packet. */
  private buildLogNoteXmp (logNote: string): string {
    const doc = document.implementation.createDocument(XMPMETA_NS, 'x:xmpmeta', null)
    const rdf = doc.createElementNS(RDF_NS, 'rdf:RDF')
    const description = doc.createElementNS(RDF_NS, 'rdf:Description')
    const logNoteEl = doc.createElementNS(LOG_NOTE_NS_URI, `${LOG_NOTE_NS}:${LOG_NOTE_COLUMN_ID}`)
    logNoteEl.textContent = logNote

    description.appendChild(logNoteEl)
    rdf.appendChild(description)
    doc.documentElement.appendChild(rdf)

    const body = new XMLSerializer().serializeToString(doc)
    // W5M0... is the XMP-spec packet marker id, identical in every packet.
    return '<?xpacket begin="\uFEFF" id="W5M0MpCehiHzreSzNTczkc9d"?>'
      + body
      + '<?xpacket end="w"?>'
  }

  getLocalPath (options) {
    const electron = require('@electron/remote')
    const win = electron.getCurrentWindow()
    const result = electron.dialog.showOpenDialogSync(win, {
      defaultPath: options.defaultPath,
      properties: options.properties,
      filters: options.filters,
    })
    return result?.[0]
  }

  getConfig (key: string) {
    return window.localStorage.getItem(key)
  }

  setConfig (key: string, value) {
    window.localStorage.setItem(key, value)
  }

  hasConfig () {
    return true
  }

  override openURL (url: string) {
    const shell = require('@electron/remote').shell
    shell.openExternal(url)
  }

  override async renderSequence (
    ready: (_: GenericFileReader) => Promise<void>,
  ): Promise<void> {
    return this.withBusyState(async () => {
      const fs = require('fs')
      const os = require('os')
      const path = require('path')

      // Remove stale files from previous imports
      const tmpdir = os.tmpdir()
      for (const name of fs.readdirSync(tmpdir)) {
        if (name.startsWith('elements-sequence-scratch.')) {
          try {
            fs.unlinkSync(path.join(tmpdir, name))
          } catch { /* ignore */ }
        }
      }

      let exported: { outputPath: string, name: string, extension: string } | null
      try {
        exported = await this.bridge.withScope(async () => {
          const project = await this.getProject()
          const sequence = await this.getActiveSequence(project)
          if (!sequence) {
            throw new Error(this.translate.instant('No active sequence to upload'))
          }

          const modal = this.ngbModal.open(PremiereRenderModalComponent)
          modal.componentInstance.pickFile = () => this.getLocalPath({
            properties: ['openFile'],
            filters: [{ name: 'Export preset', extensions: ['epr'] }],
          })
          const params: SequenceRenderParams | null = await modal.result.catch(() => null)
          if (!params) {
            return null
          }

          const exportFull = params.scope === SequenceRenderScope.Entire
          const presetPath = path.join(os.tmpdir(), 'elements-sequence-export-preset.epr')
          fs.writeFileSync(presetPath, params.presetContent)

          const encoderStatic = await this.bridge.ppro('EncoderManager')
          const manager = await encoderStatic.callRef('getManager')
          if (!manager) {
            throw new Error(this.translate.instant('Premiere encoder is unavailable'))
          }

          const extension = await this.getExportExtension(encoderStatic, sequence, presetPath)
          const outputPath = path.join(os.tmpdir(), 'elements-sequence-scratch.' + extension)
          const exportType = await encoderStatic.get<unknown>('EXPORT_IMMEDIATELY')

          const EXPORT_TIMEOUT_MS = 6 * 60 * 60 * 1000
          let ok: boolean
          try {
            ok = await manager.call<boolean>('exportSequence', [
              sequence.handle, exportType, outputPath, presetPath, exportFull,
            ], { timeoutMs: EXPORT_TIMEOUT_MS })
          } catch (error: any) {
            this.logger.error('exportSequence failed', error)
            try {
              fs.unlinkSync(outputPath)
            } catch { /* nothing to clean up */ }
            const message = error?.message ?? String(error)
            if (message.includes('Unable to initialize export')) {
              throw new Error(this.translate.instant('Unable to export. Make sure the sequence is not empty.'))
            }
            throw error
          }
          if (!ok) {
            try {
              fs.unlinkSync(outputPath)
            } catch { /* nothing to clean up */ }
            throw new Error(this.translate.instant('Premiere reported the export failed'))
          }

          if (!fs.existsSync(outputPath)) {
            throw new Error(this.translate.instant('Render produced no file, is your sequence empty?'))
          }

          return { outputPath, name: params.name, extension }
        })
      } catch (error) {
        if (error instanceof BridgeError) {
          const message = error.transport
            ? this.translate.instant('Premiere disconnected during export')
            : error.userMessage || this.translate.instant('Export failed')
          throw new Error(message)
        }
        throw error
      }

      if (!exported) {
        return
      }

      const reader = new NodeFileReader(fs, exported.outputPath, exported.name + '.' + exported.extension)
      try {
        await ready(reader)
      } catch (error) {
        if (error instanceof BridgeError) {
          throw new Error(error.userMessage || this.translate.instant('Upload failed'))
        }
        throw error
      } finally {
        reader.close()
        fs.unlinkSync(exported.outputPath)
      }
    })
  }

  async getExportExtension (encoderStatic: Ref, sequence: Ref, presetPath: string): Promise<string> {
    const ext = await encoderStatic.call<string>('getExportFileExtension', [sequence.handle, presetPath]).catch(() => null)
    if (ext) {
      return ext
    }
    return 'mp4'
  }

  async runOp<T> (label: string, op: () => Promise<T>, fallback: T): Promise<T> {
    try {
      return await op()
    } catch (error) {
      this.reportError(label, error)
      return fallback
    }
  }

  override reportError (label: string, error: unknown): void {
    this.logger.error(`${label}:`, error)

    if (error instanceof BridgeError && error.transport) {
      this.notify.error(
        this.translate.instant('Premiere is not connected'),
        this.translate.instant('Open the ELEMENTS panel in Premiere and try again.'),
      )
      return
    }

    const raw = error instanceof BridgeError
      ? error.userMessage
      : error instanceof Error ? error.message : String(error)

    const detail = raw.trim()
      ? raw
      : this.translate.instant('An unknown error occurred. See the panel console for details.')
    this.notify.error(this.translate.instant(label), detail)
  }
}
